'use client'

import { useState } from 'react'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import { Database, Server, ChevronDown } from 'lucide-react'
import { ConnectionError } from '@/lib/types/config'
import { useI18n } from '@/lib/hooks/use-i18n'

interface ConnectionErrorOverlayProps {
  error: ConnectionError
  onRetry: () => void
}

export function ConnectionErrorOverlay({
  error,
  onRetry,
}: ConnectionErrorOverlayProps) {
  const [showDetails, setShowDetails] = useState(false)
  const isApiError = error.type === 'api-unreachable'
  const { t } = useI18n()

  const reasons = isApiError
    ? [
        t('connection.guide.api.item1'),
        t('connection.guide.api.item2'),
        t('connection.guide.api.item3'),
      ]
    : [
        t('connection.guide.db.item1'),
        t('connection.guide.db.item2'),
        t('connection.guide.db.item3'),
      ]

  return (
    <div
      className="fixed inset-0 bg-background z-50 flex items-center justify-center p-4"
      role="alert"
      aria-live="assertive"
      aria-atomic="true"
    >
      <Card className="max-w-2xl w-full p-8 space-y-6">
        {/* Error icon and title */}
        <div className="flex items-center gap-4">
          {isApiError ? (
            <Server className="w-12 h-12 text-destructive" aria-hidden="true" />
          ) : (
            <Database className="w-12 h-12 text-destructive" aria-hidden="true" />
          )}
          <div>
            <h1 className="text-2xl font-bold" id="error-title">
              {isApiError
                ? t('connection.api.unreachable.title')
                : t('connection.db.offline.title')}
            </h1>
            <p className="text-muted-foreground">
              {isApiError
                ? t('connection.api.unreachable.subtitle')
                : t('connection.db.offline.subtitle')}
            </p>
          </div>
        </div>

        {/* Troubleshooting instructions */}
        <div className="space-y-4 border-l-4 border-primary pl-4">
          <h2 className="font-semibold">{t('connection.guide.heading')}</h2>
          <ul className="list-disc list-inside space-y-2 text-sm">
            {reasons.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </div>

        {/* Documentation link */}
        {/*<div className="text-sm">*/}
        {/*  <p>{t('connection.docs.prefix')}</p>*/}
        {/*  <a*/}
        {/*    href="http://git.crazylittlefish.cn/fishown-agent-v2/fishown-agent-dashboard"*/}
        {/*    target="_blank"*/}
        {/*    rel="noopener noreferrer"*/}
        {/*    className="text-primary hover:underline inline-flex items-center gap-1"*/}
        {/*  >*/}
        {/*    {t('connection.docs.link')}*/}
        {/*    <ExternalLink className="w-4 h-4" />*/}
        {/*  </a>*/}
        {/*</div>*/}

        {/* Collapsible technical details */}
        {error.details && (
          <Collapsible open={showDetails} onOpenChange={setShowDetails}>
            <CollapsibleTrigger asChild>
              <Button variant="ghost" size="sm" className="w-full justify-between">
                <span>{t('connection.details.toggle')}</span>
                <ChevronDown
                  className={`w-4 h-4 transition-transform ${
                    showDetails ? 'rotate-180' : ''
                  }`}
                />
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="pt-4">
              <div className="space-y-2 text-sm bg-muted p-4 rounded font-mono">
                {error.details.attemptedUrl && (
                  <div>
                    <strong>{t('connection.details.attemptedUrl')}</strong> {error.details.attemptedUrl}
                  </div>
                )}
                {error.details.message && (
                  <div>
                    <strong>{t('connection.details.message')}</strong> {error.details.message}
                  </div>
                )}
                {error.details.technicalMessage && (
                  <div>
                    <strong>{t('connection.details.technical')}</strong>{' '}
                    {error.details.technicalMessage}
                  </div>
                )}
                {error.details.stack && (
                  <div>
                    <strong>{t('connection.details.stack')}</strong>
                    <pre className="mt-2 overflow-x-auto text-xs">
                      {error.details.stack}
                    </pre>
                  </div>
                )}
              </div>
            </CollapsibleContent>
          </Collapsible>
        )}

        {/* Retry button */}
        <div className="pt-4 border-t">
          <Button onClick={onRetry} className="w-full" size="lg">
            {t('connection.retry')}
          </Button>
          <p className="text-xs text-muted-foreground text-center mt-2">
            {t('connection.retry.hint')}
          </p>
        </div>
      </Card>
    </div>
  )
}
